import { useState, useEffect } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MonitorPlay, ArrowRight, Eye, EyeOff, CreditCard } from "lucide-react";

const BG_URL =
  "https://static.prod-images.emergentagent.com/jobs/95e517c4-9d14-4c2e-a2b6-240cf727fed6/images/400482878e13e0e9e5f9119709d659e9af1f1a085ee3d4f63b2582d67abb6238.png";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activated, setActivated] = useState(
    new URLSearchParams(window.location.search).get("activated") === "1"
  );
  const [trialExpired, setTrialExpired] = useState(
    new URLSearchParams(window.location.search).get("expired") === "trial"
  );
  const nav = useNavigate();
  const redirectAfter = new URLSearchParams(window.location.search).get("redirect") || "/admin";

  // Cek trial expired kalau user udah login (misal refresh page setelah trial berakhir)
  useEffect(() => {
    if (trialExpired) return; // Udah dari URL param, skip API call biar token gak kehapus
    let mounted = true;
    if (user && user !== false) {
      import("@/lib/api").then(({ api }) => {
        api.get("/auth/trial-info").then(({ data }) => {
          if (mounted && data?.is_trial && data?.expired) {
            setTrialExpired(true);
          }
        }).catch(() => {});
      });
    }
    return () => { mounted = false; };
  }, [user, trialExpired]);

  // Jangan redirect kalau trial expired — biar notice merahnya kelihatan
  if (user && user !== false && !trialExpired) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await login(email.trim().toLowerCase(), password);
      if (result.trial_expired) {
        setTrialExpired(true);
        toast.error("Masa trial Anda telah berakhir. Silakan perpanjang.", { duration: 6000 });
      } else {
        toast.success("Welcome back");
        // Redirect ke halaman yang dituju sebelumnya (misal /payment?plan=xxx)
        nav(redirectAfter, { replace: true });
      }
    } catch (err) {
      const msg = formatApiError(err);
      if (msg.includes("TRIAL_EXPIRED")) {
        setTrialExpired(true);
        toast.error(msg, { duration: 8000 });
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Left: form */}
      <div className="flex items-center justify-center p-8 sm:p-16 bg-white">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-12">
            <div className="h-9 w-9 bg-[#002FA7] flex items-center justify-center">
              <MonitorPlay className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-bold tracking-tight text-lg">
              DigiSign
            </span>
          </div>
          <p className="label-eyebrow mb-3">Control Room · v1</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-none mb-3">
            Sign in to the<br />command deck.
          </h1>
          <p className="text-sm text-zinc-500 mb-10">
            Manage every screen, every promotion — from one console.
          </p>

          {activated && (
            <div className="p-3 mb-6 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-800 font-medium">
              ✅ Akun berhasil diaktifkan! Silakan login.
            </div>
          )}

          {trialExpired && (
            <div className="p-4 mb-6 bg-red-50 border border-red-200 rounded">
              <p className="text-sm font-semibold text-red-800 mb-1">
                ❌ Trial Expired
              </p>
              <p className="text-xs text-red-600 mb-3">
                Masa trial 7 hari Anda telah berakhir. Silakan pilih paket berlangganan untuk melanjutkan.
              </p>
              <Link
                to="/harga"
                className="inline-flex items-center gap-1.5 text-xs font-bold text-red-700 bg-red-100 px-3 py-1.5 hover:bg-red-200 transition-colors"
              >
                Lihat paket harga
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label className="label-eyebrow" htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-none h-11 border-zinc-300"
                required
              />
            </div>
            <div>
              <Label className="label-eyebrow" htmlFor="password">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-none h-11 border-zinc-300 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="signage-btn-primary w-full h-11 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? "Signing in…" : (<>Enter dashboard <ArrowRight className="h-4 w-4" /></>)}
            </button>
          </form>

          <p className="mt-8 text-xs text-zinc-400 text-center">
            Trial expired?{" "}
            <Link to="/harga" className="text-[#002FA7] font-semibold underline underline-offset-2">
              Lihat paket harga
            </Link>
          </p>

        </div>
      </div>

      {/* Right: hero */}
      <div className="relative hidden lg:block">
        <img
          src={BG_URL}
          alt="signage"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute inset-0 p-12 flex flex-col justify-end text-white">
          <p className="label-eyebrow text-zinc-300">Now Playing</p>
          <h2 className="font-display text-3xl sm:text-4xl font-black mt-2 max-w-md leading-tight">
            One console. Dozens of screens.<br />Zero downtime.
          </h2>
        </div>
      </div>
    </div>
  );
}
